import React, { useState, useEffect, useContext, useMemo } from 'react';
import { KatButton } from '@amzn/katal-react';
import { AuthenticatedUserContext } from '../AuthenticationController';
import appConfigApi, { UseCase } from '../../services/AppConfigDeployer/AppConfigDeployerService';
import { useAppConfigEditor } from './useAppConfigEditor';
import { useAppConfigDeployer } from './useAppConfigDeployer';
import { useAppConfigAccess } from './useAppConfigAccess';
import ConfigEditorTab from './ConfigEditorTab';
import ConfigDeployerTab from './ConfigDeployerTab';
import './AppConfigDeployer.scss';

interface Props {
    stage: string;
    region: string;
}

const AppConfigDeployer: React.FC<Props> = ({ stage, region }) => {
    const { userAlias } = useContext(AuthenticatedUserContext);

    // Access gate — hides the dashboard if the user is not in the configured team.
    const access = useAppConfigAccess(stage, region, userAlias);

    // Use cases
    const [useCases, setUseCases] = useState<UseCase[]>([]);
    const [selectedUseCase, setSelectedUseCase] = useState('');
    const [loadingUseCases, setLoadingUseCases] = useState(false);

    // Tabs
    // reviewParam is intentionally memoized with empty deps — we only read the URL query
    // param once on initial render. In our non-SPA architecture, navigation triggers a full
    // page reload, so the URL search params will not change without a remount.
    const reviewParam = useMemo(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            return params.get('review') || null;
        } catch { return null; }
    }, []);
    const [activeTab, setActiveTab] = useState(reviewParam ? 'deployer' : 'editor');
    const [highlightedChangeId] = useState<string | null>(reviewParam);

    // Hooks for each tab
    const editor = useAppConfigEditor(stage, region, selectedUseCase, userAlias);
    const deployer = useAppConfigDeployer(stage, region, userAlias);

    // Load use cases on region change — but only after access is confirmed.
    useEffect(() => {
        if (access.hasAccess !== true) return;
        let cancelled = false;
        setSelectedUseCase('');
        editor.resetEditor();
        setLoadingUseCases(true);
        appConfigApi.listUseCases(stage, region)
            .catch(() => [] as UseCase[])
            .then(uc => { if (!cancelled) setUseCases(uc); })
            .finally(() => { if (!cancelled) setLoadingUseCases(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage, region, access.hasAccess]);

    // Load deployer data when switching to deployer tab
    useEffect(() => {
        if (access.hasAccess !== true) return;
        if (activeTab === 'deployer') deployer.loadDeployerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, deployer.loadDeployerData, access.hasAccess]);

    if (access.loading || access.hasAccess === undefined) {
        return (
            <div className="appconfig-deployer appconfig-deployer--access-loading">
                Checking access...
            </div>
        );
    }
    if (!access.hasAccess) {
        return (
            <div className="appconfig-deployer appconfig-deployer--access-denied">
                <h2>Access denied</h2>
                <p>
                    You do not have access to the AppConfig Deployer dashboard. Access is gated
                    by membership in the team that owns the{' '}
                    <a href="https://bindles.amazon.com/software_app/PayStationRefunds" target="_blank" rel="noopener noreferrer">
                        PayStationRefunds bindle
                    </a>
                    . Ask the bindle owners to add you to{' '}
                    <a href="https://permissions.amazon.com" target="_blank" rel="noopener noreferrer">
                        paystation-refunds-team
                    </a>
                    .
                </p>
            </div>
        );
    }

    return (
        <div className="appconfig-deployer">
            {/* ── Tab bar ── */}
            <div className="tab-bar">
                <button
                    type="button"
                    className={`tab-btn${activeTab === 'editor' ? ' tab-btn--active' : ''}`}
                    onClick={() => { setActiveTab('editor'); if (selectedUseCase) editor.loadConfig(); }}
                >
                    Config Editor
                </button>
                <button
                    type="button"
                    className={`tab-btn${activeTab === 'deployer' ? ' tab-btn--active' : ''}`}
                    onClick={() => setActiveTab('deployer')}
                >
                    Config Deployer
                </button>
                {activeTab === 'deployer' && (
                    <div style={{ marginLeft: 'auto' }}>
                        <KatButton variant="secondary" label={deployer.loadingDeployer ? 'Refreshing...' : 'Refresh'}
                            onClick={deployer.loadDeployerData} disabled={deployer.loadingDeployer} />
                    </div>
                )}
            </div>

            {/* ── Config Editor Tab ── */}
            {activeTab === 'editor' && (
                <ConfigEditorTab
                    useCases={useCases}
                    loadingUseCases={loadingUseCases}
                    selectedUseCase={selectedUseCase}
                    onUseCaseChange={setSelectedUseCase}
                    editor={editor}
                />
            )}

            {/* ── Config Deployer Tab ── */}
            {activeTab === 'deployer' && (
                <ConfigDeployerTab
                    highlightedChangeId={highlightedChangeId}
                    deployer={deployer}
                    userAlias={userAlias}
                />
            )}
        </div>
    );
};

export default AppConfigDeployer;
